import React, { useEffect, useState } from "react";
import moment from "moment";
import { Color } from "config/colors";
import { formatNumber } from "utils/utils";
import { useTranslation } from "react-i18next";
import { TipActionsCell } from "./actionsCell";
import { TipDetailsComponent } from "./tipDetails";
import { FontFamily, FontSize } from "config/font";
import { Text } from "components/Typography/text.styled";
import { TipStatusIndicator } from "./tipStatusIndicator";
import { PayoutType, TipDetails, TipStatus, TipFilter } from "model/Tip";
import { TipState } from "web-apps/company/stores/tipStore/tipStore.reducer";
import {
  TipCell,
  TipInput,
  WeekContainer,
  TipColumnDivider,
  TipCellContainer,
} from "../tips.styled";

type TipListProps = {
  tipState: TipState;
  tips: TipDetails[];
  selectedFilter: TipFilter;
  loadingIds: number[];
  selectedTips: number[];
  selectedTipIds: number[];
  expandedTipIds: number[];
  isMultiSelectActive: boolean;
  remainingAmounts: Record<string, number>;
  tipInputValues: { [tipId: number]: string };
  setTipInputValues: React.Dispatch<
    React.SetStateAction<{ [tipId: number]: string }>
  >;
  handleDelete: (tipId: number) => void;
  toggleDropdown: (tipId: number) => void;
  toggleSelectTip: (tipId: number) => void;
  openCalculateModal: (tipId: number) => void;
  handlePayout: (tipId: number, payoutType: PayoutType) => void;
  getWorkerStatus: (workerId: number, date: string) => JSX.Element;
  handleTipAmountChange: (
    tipId: number,
    value: string,
    date: string,
    onConfirm: () => void,
    onCancel: () => void,
  ) => void;
};

export const TipList: React.FC<TipListProps> = (props) => {
  const { t } = useTranslation();
  const [tipsWithWeeks, setTipsWithWeeks] = useState<{
    [week: number]: TipDetails[];
  }>({});

  useEffect(() => {
    if (!props.tips || props.tips.length === 0) {
      setTipsWithWeeks({});
      return;
    }

    const grouped: { [week: number]: TipDetails[] } = {};

    props.tips.forEach((tip) => {
      const weekNumber = moment(tip.date).isoWeek();
      if (!grouped[weekNumber]) {
        grouped[weekNumber] = [];
      }
      grouped[weekNumber].push(tip);
    });

    setTipsWithWeeks(grouped);
  }, [props.tips]);

  const handleTipInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tip: TipDetails,
  ) => {
    const newValue = e.target.value;
    const tipId = tip.companyTipId;

    if (!/^\d*$/.test(newValue)) return;

    props.setTipInputValues((prev) => ({
      ...prev,
      [tipId]: newValue,
    }));

    props.handleTipAmountChange(
      tipId,
      newValue,
      tip.date,
      () => {},
      () => {
        props.setTipInputValues((prev) => ({
          ...prev,
          [tipId]: tip.amount.toString(),
        }));
      },
    );
  };

  const isInputDisabled = (): boolean =>
    props.selectedFilter === TipFilter.Saved ||
    props.selectedFilter === TipFilter.ReadyToPayout;

  const closedTip = (tip: TipDetails): boolean => {
    return (
      tip.status === TipStatus.Closed || tip.status === TipStatus.ClosedManually
    );
  };

  return (
    <>
      {Object.keys(tipsWithWeeks).length === 0 ? (
        <Text>{t("CompanyTip.NoPeriodsWithChosenFilter")}</Text>
      ) : (
        Object.entries(tipsWithWeeks)
          .sort(([, aTips], [, bTips]) => {
            const aMin = moment.min(aTips.map((tip) => moment(tip.date)));
            const bMin = moment.min(bTips.map((tip) => moment(tip.date)));
            return aMin.diff(bMin);
          })
          .map(([weekNumber, weekTips]) => (
            <div key={weekNumber}>
              {weekTips.map((tip: TipDetails, index: number) => {
                const remainingAmount =
                  props.remainingAmounts[tip.companyTipId] ??
                  tip.remainingAmount;

                const isPartiallyRemaining =
                  remainingAmount > 0 && remainingAmount < tip.amount;

                return (
                  <div key={tip.companyTipId}>
                    {index === 0 && (
                      <WeekContainer>
                        <Text
                          style={{ margin: "8px" }}
                          fontFamily={FontFamily.MontserratSemiBold}
                        >
                          {t("CompanyTip.Week")} {weekNumber}
                        </Text>
                      </WeekContainer>
                    )}
                    <TipCellContainer
                      isSelected={props.selectedTipIds.includes(
                        tip.companyTipId,
                      )}
                      isExpanded={props.expandedTipIds.includes(
                        tip.companyTipId,
                      )}
                    >
                      <TipCell>
                        <Text fontSize={FontSize.Large}>
                          {moment(tip.date).format("ddd").toUpperCase()}{" "}
                          {moment(tip.date).format("DD/MM")}
                        </Text>
                      </TipCell>
                      <TipColumnDivider />
                      <TipCell>
                        <TipInput
                          style={{ marginLeft: "8px", marginRight: "8px" }}
                          width="16vh"
                          type="text"
                          maxLength={5}
                          placeholder="0"
                          disabled={closedTip(tip) || isInputDisabled()}
                          value={
                            props.tipInputValues[tip.companyTipId] ??
                            (tip.amount === 0 ? "" : tip.amount.toString())
                          }
                          onChange={(e) => handleTipInputChange(e, tip)}
                        />
                      </TipCell>
                      <TipColumnDivider />
                      <TipCell>
                        <Text
                          fontSize={FontSize.Large}
                          color={
                            isPartiallyRemaining
                              ? Color.LighterDestructive
                              : Color.MidnightBlue
                          }
                          title={
                            isPartiallyRemaining
                              ? t("CompanyTip.YouStillHaveTipToDistribute")
                              : ""
                          }
                        >
                          {formatNumber(
                            props.remainingAmounts[tip.companyTipId] ??
                              tip.remainingAmount,
                          )}{" "}
                          {t("CompanyTip.Kr")}
                        </Text>
                      </TipCell>
                      <TipColumnDivider />
                      <TipCell>
                        <TipStatusIndicator status={tip.status} />
                      </TipCell>
                      <TipActionsCell
                        tip={tip}
                        isMultiSelectActive={props.isMultiSelectActive}
                        isExpanded={props.expandedTipIds.includes(
                          tip.companyTipId,
                        )}
                        isSelected={props.selectedTips.includes(
                          tip.companyTipId,
                        )}
                        isDeleting={props.loadingIds.includes(tip.companyTipId)}
                        onToggleDropdown={() =>
                          props.toggleDropdown(tip.companyTipId)
                        }
                        onToggleSelect={() =>
                          props.toggleSelectTip(tip.companyTipId)
                        }
                        onDelete={() => props.handleDelete(tip.companyTipId)}
                      />
                    </TipCellContainer>
                    <TipDetailsComponent
                      tip={tip}
                      tipState={props.tipState}
                      handlePayout={props.handlePayout}
                      getWorkerStatus={props.getWorkerStatus}
                      openDistributeModal={props.openCalculateModal}
                      isExpanded={props.expandedTipIds.includes(
                        tip.companyTipId,
                      )}
                    />
                  </div>
                );
              })}
            </div>
          ))
      )}
    </>
  );
};
